'use client';

import MetLogo from 'assets/icons/met-logo.svg';
import { Link } from 'src/navigation';
import pushToDataLayer from 'lib/analytics/pushToDataLayer';
import { CURRENT_YEAR } from 'lib/constants.mjs';

import styles from './global-footer.module.scss';
import { socialLinks } from './footer-data';
import { type SanityAddress } from '@metmuseum/types';
import NewsLetterSignUpBox from './news-letter';
import LanguageList from '../language-list';
import { ensureEngageLocale } from 'lib/engage';
import { useLocale } from 'next-intl';

const handleMenuAnalytics = (e) => {
	pushToDataLayer({
		event: 'menu_click',
		menu_category_1: e.target?.innerText,
		menu_item_location: 'footer',
	});
};

interface IBuildingProps {
	_id: string;
	address: SanityAddress;
	phoneNumber: string;
	title: string;
}

export interface IProps {
	footerData: {
		categories: [
			{
				_key: string;
				links?: { _key: string; title: string; url: string }[];
				url: string;
				title: string;
			},
		];
	};
	buildings: {
		metFifth: IBuildingProps;
		cloisters: IBuildingProps;
	};
}

interface IBuildingInfoProps {
	building: IBuildingProps;
	slug: string;
}

export const GlobalFooter = ({ buildings, footerData }: IProps) => {
	const locale = useLocale();

	return (
		<footer className={styles.footer}>
			<div className={styles.mainContent}>
				<section className={styles.locations}>
					<Link title="Homepage" href="/" className={styles.logoLink}>
						<MetLogo style={{ height: 40, width: 40 }} />
					</Link>
					<div className={styles.contact}>
						<ul className={styles.locationItems}>
							<BuildingInfo
								building={buildings.metFifth}
								slug="met-fifth-avenue"
							/>
							<BuildingInfo
								building={buildings.cloisters}
								slug="met-cloisters"
							/>
						</ul>
						<SocialLinks />
					</div>
				</section>
				<section className={styles.linksAndNewsletter}>
					<div className={styles.linkContainer}>
						{footerData?.categories?.map((category) => {
							return (
								<div key={category._key} className={styles.links}>
									<ul className={styles.navItems}>
										<li className={styles.navItem}>
											<Link
												prefetch={false}
												href={category.url}
												onClick={handleMenuAnalytics}
												className={styles.link}
											>
												{category.title}
											</Link>
										</li>
										{category.links?.map((link) => {
											return (
												<li key={link._key} className={styles.navItem}>
													<Link
														prefetch={false}
														href={ensureEngageLocale(link.url, locale)}
														onClick={handleMenuAnalytics}
														className={styles.link}
													>
														{link.title}
													</Link>
												</li>
											);
										})}
									</ul>
								</div>
							);
						})}
					</div>
					<NewsLetterSignUpBox />
				</section>
			</div>
			<section className={styles.locales}>
				<LanguageList />
			</section>
			<section className={styles.info}>
				<ul className={styles.infoItems}>
					{[
						{
							path: '/information/terms-and-conditions',
							title: 'Terms and Conditions',
						},
						{ path: '/information/privacy-policy', title: 'Privacy Policy' },
						{ path: '/about-the-met/contact', title: 'Contact Information' },
					].map((infoItem) => {
						return (
							<li key={infoItem.title} className={styles.infoItem}>
								<Link
									prefetch={false}
									href={infoItem.path}
									onClick={handleMenuAnalytics}
									className={styles.link}
								>
									{infoItem.title}
								</Link>
							</li>
						);
					})}
				</ul>
				<div className={styles.copyrightInfo}>
					© 2000&ndash;{CURRENT_YEAR} The Metropolitan Museum of Art. All rights
					reserved.
				</div>
			</section>
		</footer>
	);
};

const SocialLinks = () => {
	return (
		<ul className={styles.socialLinks}>
			{socialLinks.map((socialLink) => {
				return (
					<li key={socialLink.siteName} className={styles.socialItem}>
						<Link
							prefetch={false}
							title={socialLink.title}
							href={socialLink.url}
							target="_blank"
							className={styles.socialLink}
							onClick={() => {
								pushToDataLayer({
									event: 'menu_click',
									menu_category_1: 'Social',
									menu_category_2: socialLink.siteName,
									menu_item_location: 'footer',
								});
							}}
							rel="noreferrer"
						>
							{socialLink.icon}
						</Link>
					</li>
				);
			})}
		</ul>
	);
};

const BuildingInfo = ({ building, slug }: IBuildingInfoProps) => {
	return (
		<li>
			<div className={styles.museumAddressName}>
				<Link
					className={styles.link}
					href={`/plan-your-visit/${slug}`}
					onClick={handleMenuAnalytics}
				>
					<span className={styles.locationTitle}>{building.title}</span>
				</Link>
			</div>
			<address>
				<div>
					<div>{building?.address?.addressLine1}</div>
					<div>
						{building?.address?.city}, {building?.address?.state}{' '}
						{building?.address?.zipcode}
					</div>
					<div>Phone: {building.phoneNumber}</div>
				</div>
			</address>
		</li>
	);
};

export default GlobalFooter;
